<template>
  <div class="announcement-info">
    <template v-if="detail">
      <!-- 标题（详情内容 = 标题 + 附件） -->
      <div class="info-card">
        <h1 class="info-title">{{ detail.title }}</h1>
      </div>

      <!-- 图片附件：逐张完整展示，点击可放大 -->
      <div class="pic-list" v-if="images.length">
        <div class="pic-item" v-for="(item, index) in images" :key="'pic' + index">
          <!-- 微信浏览器用微信图片预览 -->
          <img v-if="isWeixin" :src="item.url" :alt="item.name" @click="previewImage(index)">
          <!-- 普通浏览器由 vue-photo-preview 接管 -->
          <img v-else :src="item.url" :alt="item.name" preview="1" preview-text="">
        </div>
      </div>

      <!-- 其它附件（pdf / 文档等）：列表形式，点击查看 -->
      <div class="annex-card" v-if="docs.length">
        <div class="annex-head">附件（{{ docs.length }}）</div>
        <div
          class="annex-item"
          v-for="(item, index) in docs"
          :key="'doc' + index"
          @click="openAnnex(item)"
        >
          <span class="annex-icon" :class="extOf(item) ? 'is-' + extOf(item) : ''">{{ extOf(item) || '文件' }}</span>
          <div class="annex-body">
            <p class="annex-name">{{ item.name || ('附件' + (index + 1)) }}</p>
            <p class="annex-size" v-if="sizeText(item.size)">{{ sizeText(item.size) }}</p>
          </div>
          <span class="annex-view">查看</span>
        </div>
      </div>
    </template>

    <!-- 缺少参数 / 数据已失效 -->
    <div class="page-state is-empty" v-else>
      <p>{{ emptyText }}</p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import "../../common/sass/mixin.scss";

  .announcement-info {
    width: 100%;
    min-height: 100vh;
    background-color: #f4efe5;
    box-sizing: border-box;
    /* 顶部不再有固定头部，只留少量留白 */
    padding: 12px 0 48px;
  }

  /* 标题卡片 */
  .info-card {
    margin: 0 14px;
    background-color: #fff;
    border-radius: 14px;
    padding: 16px 18px;
    box-shadow: 0 6px 16px rgba(93, 62, 31, 0.08);
  }
  .info-title {
    margin: 0;
    font-family: "Microsoft YaHei", "PingFang SC", sans-serif;
    font-size: 17px;
    font-weight: bold;
    color: #2b2118;
    line-height: 1.5;
    letter-spacing: 0.5px;
  }

  /* 图片附件：逐张完整展示 */
  .pic-list {
    margin-top: 12px;
  }
  .pic-item {
    margin: 0 14px 12px;
    border-radius: 12px;
    overflow: hidden;
    background-color: #fff;
    box-shadow: 0 6px 16px rgba(93, 62, 31, 0.06);
    img {
      display: block;
      width: 100%;
      height: auto;
    }
  }

  /* 附件列表 */
  .annex-card {
    margin: 12px 14px 0;
    background-color: #fff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 6px 16px rgba(93, 62, 31, 0.06);
  }
  .annex-head {
    padding: 14px 16px 10px;
    font-size: 14px;
    color: #9a8c78;
    letter-spacing: 1px;
  }
  .annex-item {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    cursor: pointer;
    & + .annex-item {
      border-top: 1px solid #f3f0e9;
    }
    &:active {
      background-color: #faf7f1;
    }
  }
  .annex-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
    color: #fff;
    text-transform: uppercase;
    background-color: #b9ab96;
    &.is-pdf {
      background-color: #C91B1F;
    }
  }
  .annex-body {
    flex: 1;
    min-width: 0;
    padding: 0 12px;
  }
  .annex-name {
    margin: 0;
    font-family: "Microsoft YaHei", "PingFang SC", sans-serif;
    font-size: 15px;
    color: #32291e;
    line-height: 1.5;
    @include ellipsis(2);
  }
  .annex-size {
    margin: 4px 0 0;
    font-size: 12px;
    color: #9a8c78;
  }
  .annex-view {
    flex-shrink: 0;
    font-size: 13px;
    color: #C91B1F;
  }

  /* 空数据状态 */
  .page-state {
    padding: 80px 0 60px;
    text-align: center;
    background-repeat: no-repeat;
    background-position: center;
    background-image: url("../../assets/default.png");
    background-size: 100px 100px;
    p {
      margin: 120px 0 0;
      font-family: "Microsoft YaHei", "PingFang SC", sans-serif;
      font-size: 15px;
      color: #9a8c78;
    }
  }
</style>

<script>
  import wx from 'weixin-js-sdk'
  import {IsWeiXin} from '../../common/js/isWeixin'
  import {loadAnnouncementList} from '../../common/js/cache.js'
  import {splitFiles, getFileExt, formatFileSize, openFile} from '../../common/js/annex.js'

  // 从列表页缓存的整组公告里按 id 取出本条
  function pickDetail(list, id) {
    if (!Array.isArray(list)) return null
    return list.find(item => item && String(item.id) === id) || null
  }

  export default {
    name: 'MeetingAnnouncementInfo',
    data() {
      return {
        // 公告分组码与公告 id：来自链接（/home/MeetingAnnouncementInfo/xxx/12）
        code: '',
        id: '',
        detail: null,
        emptyText: '抱歉！暂无数据',
        isWeixin: false
      }
    },
    computed: {
      // 图片附件
      images() {
        return splitFiles(this.detail && this.detail.files).images
      },
      // 其它附件（pdf / 文档等）
      docs() {
        return splitFiles(this.detail && this.detail.files).docs
      }
    },
    created() {
      this.isWeixin = IsWeiXin()
      const rawCode = this.$route.query.code != null ? this.$route.query.code : this.$route.params.code
      const rawId = this.$route.query.id != null ? this.$route.query.id : this.$route.params.id
      this.code = rawCode == null ? '' : String(rawCode).trim()
      this.id = rawId == null ? '' : String(rawId).trim()
      if (!this.code || !this.id) {
        this.emptyText = '缺少公告参数，请通过完整链接打开'
        return
      }
      // 详情不请求接口：列表页已把整组公告缓存下来，这里按 id 取本条
      this.detail = pickDetail(loadAnnouncementList(this.code), this.id)
      if (!this.detail) {
        this.emptyText = '公告数据已失效，请返回公告列表重新打开'
        return
      }
      this.emptyText = '抱歉！暂无数据'
      this.$nextTick(() => {
        // 普通浏览器的图片预览需要刷新一次
        if (this.$previewRefresh) this.$previewRefresh()
      })
    },
    methods: {
      // 图片放大：微信内调用微信图片预览
      previewImage(index) {
        const urls = this.images.map(item => item.url)
        wx.previewImage({
          current: urls[index],
          urls: urls
        })
      },
      // 附件扩展名（用于图标文字与配色）
      extOf(item) {
        return getFileExt(item)
      },
      // 字节数转可读大小（接口未返回 size 时展示空）
      sizeText(size) {
        return formatFileSize(size)
      },
      // 点击附件：pdf 等文件直接打开查看
      openAnnex(item) {
        openFile(item && item.url)
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/announcement/MeetingAnnouncementInfo.vue